import {
  REENTRY_PLANNING_TAB_CONFIG,
  REENTRY_PLANNING_TABS,
  REENTRY_PLANS_LIST_SCOPES,
  REENTRY_PLAN_TYPES,
  toVisibilityPlanType,
} from '../constants/reentry-planning.constants';

const [DEFAULT_REENTRY_PLANNING_TAB] = REENTRY_PLANNING_TAB_CONFIG;

/** Tab metadata (label, list scope, aria ids) for the active tab; falls back to the default tab. */
export const resolveReentryPlanningTab = (activeTabLabel) =>
  REENTRY_PLANNING_TAB_CONFIG.find((tab) => tab.label === activeTabLabel) ??
  DEFAULT_REENTRY_PLANNING_TAB;

export const isMyPlansScope = (listScope) => listScope === REENTRY_PLANS_LIST_SCOPES.MY;

const TAB_VISIBILITY_PREDICATES = {
  [REENTRY_PLANNING_TABS.MY_PLANS]: ({ canViewMyPlans }) => Boolean(canViewMyPlans),
  [REENTRY_PLANNING_TABS.ALL_PLANS]: ({ canViewAllPlansList }) => Boolean(canViewAllPlansList),
};

const isReentryPlanningTabVisible = (visibility, tab) => {
  const isVisible = TAB_VISIBILITY_PREDICATES[tab.label];
  return isVisible ? isVisible(visibility) : false;
};

/**
 * Filters FolderNav tabs so My Plans only appears for assigned-detail holders.
 * All Plans stays available for module access, including preview-release staff.
 */
export const resolveVisibleReentryPlanningTabs = ({
  canViewAllPlansList = false,
  canViewMyPlans = false,
  folderTabs = [],
} = {}) =>
  folderTabs.filter((tab) => isReentryPlanningTabVisible({ canViewAllPlansList, canViewMyPlans }, tab));

/** Keeps the active tab on a visible one; falls back to the first visible tab. */
export const resolveActiveReentryPlanningTabLabel = (activeTabLabel, visibleTabs = []) => {
  const matchingTab = visibleTabs.find((tab) => tab.value === activeTabLabel);
  return matchingTab?.value ?? visibleTabs[0]?.value ?? DEFAULT_REENTRY_PLANNING_TAB.label;
};

/**
 * Shared View Plan rule.
 * My Plans requires the assigned-detail (list) permission; every row on that tab is an
 * editor plan, so `isEditor` is not required.
 * All Plans shows View Plan when:
 * - reentry LFA + view-any-detail (every row), or
 * - preview LFA + preview-release and the row is a preview plan type, or
 * - assigned-detail and the current user is an editor of that row (`isEditor`).
 * Hidden when the permission is missing — never shown disabled.
 */
const isPreviewPlanFeature = (planType) =>
  toVisibilityPlanType(planType) === REENTRY_PLAN_TYPES.PREVIEW.feature;

const resolveViewPlanAvailability = ({
  canViewAssignedPlans,
  canViewAnyPlans,
  canViewPreviewPlans,
  isEditor,
  listScope,
  planType,
}) => {
  if (isMyPlansScope(listScope)) {
    return Boolean(canViewAssignedPlans);
  }

  if (canViewAnyPlans) {
    return true;
  }

  if (canViewPreviewPlans && isPreviewPlanFeature(planType)) {
    return true;
  }

  return Boolean(canViewAssignedPlans) && Boolean(isEditor);
};

/**
 * Whether a single row shows View Plan.
 *
 * @param {boolean} canViewAssignedPlans - Holder of the assigned-detail / list permission.
 * @param {boolean} canViewAnyPlans - Holder of case_plan_reentry_view_any_detail with MVP LFA.
 * @param {boolean} canViewPreviewPlans - Holder of preview-release with preview LFA.
 * @param {boolean} isEditor - Whether the current user is an editor of this plan.
 * @param {string} listScope - Active tab scope (`my` or `all`).
 * @param {string} [planType] - List API planType (`reentry` / `reentry_preview`).
 * @returns {boolean} True when the row's View Plan action should render.
 */
export const resolveCanViewPlanRow = ({
  canViewAssignedPlans = false,
  canViewAnyPlans = false,
  canViewPreviewPlans = false,
  isEditor = false,
  listScope,
  planType,
} = {}) =>
  resolveViewPlanAvailability({
    canViewAssignedPlans,
    canViewAnyPlans,
    canViewPreviewPlans,
    isEditor,
    listScope,
    planType,
  });

/**
 * Whether the action column renders at all.
 *
 * All Plans keeps the column for view-any holders even on an empty page. Assigned-detail
 * holders only need the column when the current page has at least one editor row.
 *
 * @param {boolean} canViewAssignedPlans - Holder of the assigned-detail / list permission.
 * @param {boolean} canViewAllPlans - Page-level grant (view-any or preview-release) for the action column.
 * @param {boolean} hasViewableRow - True when at least one mapped row has a View Plan action.
 * @param {string} listScope - Active tab scope (`my` or `all`).
 * @returns {boolean} True when the action column should render.
 */
export const resolveCanViewPlanColumn = ({
  canViewAssignedPlans = false,
  canViewAllPlans = false,
  hasViewableRow = false,
  listScope,
} = {}) =>
  isMyPlansScope(listScope)
    ? Boolean(canViewAssignedPlans)
    : Boolean(canViewAllPlans) || Boolean(hasViewableRow);
